import { ImageViewer } from "@/components/ImageViewer";
import { useAutoFitPdfHeight } from "@/components/Pdf/useAutoFitPdfHeight";
import { usePdfViewer } from "@/components/Pdf/usePdfViewer";
import { BodyText, Button, Callout, Skeleton } from "@/components/ui";
import { useS3Download } from "@/network/useS3Download";
import { useParams } from "@tanstack/react-router";
import type * as fhirTypes from "fhir/r4";
import { ExternalLink, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { useMemo } from "react";
import { Document, Page } from "react-pdf";

interface FileViewerProps {
  existingFile: fhirTypes.DocumentReference | null;
  newFile: File | null;
}

export const FileViewer = ({ existingFile, newFile }: FileViewerProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const { data, isLoading } = useS3Download({
    patientId: patientId as string,
    path: existingFile?.content?.[0]?.attachment?.url || "",
    enabled: Boolean(existingFile?.content?.[0]?.attachment?.url),
  });

  const { containerRef, containerWidth, pageHeight, remeasure } =
    useAutoFitPdfHeight({
      bottomMarginPx: 128,
      minHeight: 384,
    });

  // either render new file if uploaded - or fallback to existing file
  const src = useMemo(() => {
    if (newFile) {
      remeasure();
      return URL.createObjectURL(newFile);
    }
    if (data) {
      remeasure();
      return URL.createObjectURL(data);
    }
    return "";
  }, [newFile, data, remeasure]);
  const type = newFile?.type || data?.type || "";
  const pdf = type === "application/pdf";

  const {
    currentPage,
    numPages,
    scale,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    zoomIn,
    zoomOut,
    resetZoom,
    openInNewTab,
    setPageRef,
  } = usePdfViewer({
    src,
    containerRef,
  });

  // previously fetched file is already cached in react-query
  if (!data && !newFile && !isLoading) {
    return (
      <Callout
        className="mt-2"
        variant="danger"
        title="Something went wrong!"
        description="No file found."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="w-full bg-secondary mt-3 flex justify-center items-start min-h-0">
        <div className="relative w-full" style={{ height: pageHeight }}>
          <div ref={containerRef} className="w-full h-full overflow-auto p-4">
            <Skeleton
              style={{
                width: Math.floor(containerWidth * scale),
                height: `calc(${pageHeight}px - 48px)`,
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-secondary mt-3 flex justify-center items-start min-h-0">
      {!pdf ? (
        <ImageViewer src={src} />
      ) : (
        <div className="relative w-full" style={{ height: pageHeight }}>
          <div className="absolute right-3 top-3 z-10">
            <Button
              variant="secondary"
              size="md"
              onClick={() => openInNewTab()}
              className="shadow-md"
              leftIcon={ExternalLink}
            >
              Open in New Tab
            </Button>
          </div>
          {numPages ? (
            <div className="absolute left-3 top-3 z-10 bg-black/70 px-3 py-1.5 rounded-lg">
              <BodyText size="sm" textColor="white">
                {`Page ${currentPage} of ${numPages}`}
              </BodyText>
            </div>
          ) : null}
          {scale !== 1.0 && (
            <div className="absolute right-3 bottom-16 z-10 bg-black/70 px-3 py-1.5 rounded-lg">
              <BodyText
                size="sm"
                textColor="white"
              >{`${(scale * 100).toFixed(0)}%`}</BodyText>
            </div>
          )}
          <div className="absolute right-3 bottom-3 z-10 flex gap-2">
            {scale !== 1.0 && (
              <Button
                variant="secondary"
                size="icon"
                onClick={() => resetZoom()}
                className="shadow-lg"
                aria-label="Reset zoom"
              >
                <RotateCcw className="w-5 h-5 text-button-secondary" />
              </Button>
            )}
            <Button
              variant="primary"
              size="icon"
              onClick={() => zoomOut()}
              className="shadow-lg"
              aria-label="Zoom out"
            >
              <ZoomOut className="w-5 h-5 text-white" />
            </Button>
            <Button
              variant="primary"
              size="icon"
              onClick={() => zoomIn()}
              className="shadow-lg"
              aria-label="Zoom in"
            >
              <ZoomIn className="w-5 h-5 text-white" />
            </Button>
          </div>
          <div ref={containerRef} className="w-full h-full overflow-auto p-4">
            <div
              className="mx-auto"
              style={{ width: Math.floor(containerWidth * scale) }}
            >
              <Document
                file={src}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <Skeleton
                    style={{
                      width: Math.floor(containerWidth * scale),
                      height: `calc(${pageHeight}px - 48px)`,
                    }}
                  />
                }
                className="flex flex-col"
              >
                {numPages &&
                  containerWidth > 0 &&
                  Array.from({ length: numPages }, (_, i) => (
                    <div
                      key={`page_wrap_${i + 1}`}
                      ref={setPageRef(i)}
                      className="mb-4 last-of-type:mb-0 shadow-lg"
                    >
                      <Page
                        pageNumber={i + 1}
                        width={Math.floor(containerWidth * scale)}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={null}
                      />
                    </div>
                  ))}
              </Document>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
